<script setup lang="ts">
import { computed } from 'vue';

import type { ButtonSeverity, ButtonSize } from '@components/buttons/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS } from '@modules/payment-system-details/constants';
import { EReviewPaymentSystemActionType } from '@modules/payment-system-details/types';

import type { IRejectPaymentSystemForm, IRejectPaymentSystemPopupEmits, IRejectPaymentSystemPopupProps } from './types';

const props = defineProps<IRejectPaymentSystemPopupProps>();
defineEmits<IRejectPaymentSystemPopupEmits>();

const rejectionReason = defineModel<string>('rejectionReason');

const { getTranslation } = useI18n();

const schema = computed<FormSchema<IRejectPaymentSystemForm>>(() => ({
  rejection_reason: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, `${EReviewPaymentSystemActionType.REJECT}_MODAL_REASON_LABEL`).value,
    required: true,
  },
}));

const isDialogType = computed<boolean>(() => props.type === 'dialog');

const defaultBtnSize = computed<ButtonSize | undefined>(() => isDialogType.value ? undefined : 'small');

const submitBtnSeverity = computed<ButtonSeverity>(() => (
  props.options?.acceptProps && 'severity' in props.options.acceptProps
    ? props.options?.acceptProps?.severity as ButtonSeverity
    : undefined
));

const submitBtnSize = computed<ButtonSize>(() => (
  props.options?.acceptProps && 'size' in props.options.acceptProps
    ? props.options?.acceptProps?.size as ButtonSize
    : defaultBtnSize.value
));

function onChange(key: keyof IRejectPaymentSystemForm, value: string | undefined): void {
  rejectionReason.value = value;
}
</script>

<template>
  <div v-data-qa-auto-id="'VRejectPaymentSystemPopup'" :class="isDialogType ? 'p-5' : 'p-4'">
    <div
      class="flex items-center justify-between gap-4"
      :class="isDialogType ? 'pb-5' : 'pb-3'"
    >
      <p
        class="break-words"
        :class="isDialogType ? 'p-dialog-title' : 'text-lg font-medium'"
      >
        {{ options?.header }}
      </p>
      <VIconButton
        v-if="isDialogType"
        icon="pi-times"
        severity="secondary"
        @click="$emit('close')"
      />
    </div>
    <div
      class="flex items-center break-words pb-5"
      :class="isDialogType ? 'gap-4' : 'gap-2'"
    >
      <i
        v-if="options?.icon"
        :class="`${options?.icon} ${isDialogType ? 'text-3xl' : 'text-xl'}`"
      />
      <p>{{ options?.message }}</p>
    </div>
    <VForm
      :schema="schema"
      :submit-btn-label="options?.acceptLabel"
      :submit-btn-icon="options?.acceptIcon"
      :submit-btn-size="submitBtnSize"
      :submit-btn-severity="submitBtnSeverity"
      reverse-buttons
      @change="onChange"
      @submit="$emit('submit')"
    >
      <template #secondaryBtn>
        <VButton
          v-bind="options?.rejectProps"
          :label="options?.rejectLabel"
          :icon="options?.rejectIcon"
          :class="options?.rejectClass"
          :size="defaultBtnSize"
          @click="$emit('close')"
        />
      </template>
    </VForm>
  </div>
</template>
